import { FC, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useUpdateUserProfileApi, useUserProfileApi } from '@/api/users';
import { DataTable } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Text } from '@/components/ui/text';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useInviteUserToWorkspaceService,
  useRemoveUserFromWorkspaceService,
} from '@/services/settings';
import { useWorkspacesService } from '@/services/workspaces';
import type { ColumnDef } from '@tanstack/react-table';
import * as EmailValidator from 'email-validator';
import { Mail, MoreHorizontal, Trash2 } from 'lucide-react';

export const MyAccountTab: FC = () => {
  const { data: userProfile } = useUserProfileApi();
  const updateUserProfile = useUpdateUserProfileApi();
  const { workspaces } = useWorkspacesService();
  const { workspaceUname: currentWorkspaceUname } = useParams();
  const { inviteUserToWorkspace, isLoading: isInviting } =
    useInviteUserToWorkspaceService();
  const { removeUser, isRemoving } = useRemoveUserFromWorkspaceService();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');

  const currentWorkspace = workspaces?.find(
    w => w.uname === currentWorkspaceUname
  );

  useEffect(() => {
    if (userProfile) {
      setFirstName(userProfile.firstName || '');
      setLastName(userProfile.lastName || '');
    }
  }, [userProfile]);

  const handleSave = () => {
    if (firstName.trim() === '' || lastName.trim() === '') {
      toast.error('First name and last name are required.');
      return;
    }
    updateUserProfile.mutate({
      firstName: firstName || undefined,
      lastName: lastName || undefined,
    });
  };

  const isFormValid = firstName.trim() !== '' && lastName.trim() !== '';

  const inviteValidation = useMemo(() => {
    const isEmailValid =
      inviteEmail.trim() !== '' && EmailValidator.validate(inviteEmail);
    const hasFirstName = inviteFirstName.trim() !== '';
    const hasLastName = inviteLastName.trim() !== '';
    const isValid = isEmailValid && hasFirstName && hasLastName;

    let error = '';
    if (!hasFirstName || !hasLastName) {
      error = 'First name, last name, and email are required';
    } else if (!isEmailValid) {
      error = 'Email format is invalid';
    }

    return { isValid, error };
  }, [inviteEmail, inviteFirstName, inviteLastName]);

  const handleInviteUser = () => {
    if (!inviteValidation.isValid || !currentWorkspace) return;

    inviteUserToWorkspace(
      {
        email: inviteEmail,
        firstName: inviteFirstName.trim(),
        lastName: inviteLastName.trim(),
      },
      {
        onSuccess: () => {
          toast.success('User invited successfully');
          setInviteEmail('');
          setInviteFirstName('');
          setInviteLastName('');
        },
        onError: (error: any) => {
          const errorMessage =
            error?.message ||
            error?.response?.body?.message ||
            error?.response?.data?.message ||
            'Failed to invite user.';
          toast.error(errorMessage);
        },
      }
    );
  };

  const handleRemoveMember = (memberId: string, memberEmail: string) => {
    if (
      !window.confirm(
        `Remove ${memberEmail} from this workspace? They will lose access.`
      )
    )
      return;

    removeUser(memberId, {
      onSuccess: () => toast.success(`${memberEmail} removed from workspace`),
      onError: () => toast.error('Failed to remove member'),
    });
  };

  type Member = {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };

  const memberColumns = useMemo<ColumnDef<Member>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Text variant="body-sm">
            {row.original.firstName} {row.original.lastName}
          </Text>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => <Text variant="body-sm">{row.original.email}</Text>,
      },
      {
        id: 'actions',
        meta: { headerClassName: 'w-[50px]' },
        cell: ({ row }) => {
          const member = row.original;
          const isCurrentUser = member.email === userProfile?.email;
          if (isCurrentUser) return null;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  disabled={isRemoving}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => handleRemoveMember(member.id, member.email)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove from workspace
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [userProfile?.email, isRemoving, handleRemoveMember]
  );

  return (
    <FlexCol gap="8">
      {/* Personal information */}
      <FlexCol gap="5">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            Personal information
          </h2>
        </div>

        <FlexCol gap="3" className="max-w-[320px]">
          <div className="space-y-2">
            <Label htmlFor="firstName">First name</Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              placeholder="Enter your first name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName">Last name</Label>
            <Input
              id="lastName"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              placeholder="Enter your last name"
            />
          </div>
        </FlexCol>

        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <div className="w-fit">
              <Button
                onClick={handleSave}
                disabled={updateUserProfile.isPending || !isFormValid}
                className="w-fit"
              >
                {updateUserProfile.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </TooltipTrigger>
          {!isFormValid && (
            <TooltipContent>
              <Text variant="body-xs">
                First name and last name are required.
              </Text>
            </TooltipContent>
          )}
        </Tooltip>
      </FlexCol>

      {/* Workspace section */}
      {currentWorkspace && (
        <FlexCol gap="5">
          <div>
            <Text
              variant="body-xs"
              color="secondary"
              className="uppercase tracking-widest font-semibold"
            >
              Workspace
            </Text>
            <h3 className="text-lg font-semibold mt-1">
              {currentWorkspace.name}
            </h3>
            <Text variant="body-sm" color="secondary">
              Invite members to your workspace
            </Text>
          </div>

          {/* Members table */}
          <DataTable
            columns={memberColumns}
            data={currentWorkspace.members}
            hidePagination
            options={{ getRowId: row => row.id }}
          />

          {/* Invite form */}
          <div className="flex gap-2 max-w-lg items-end">
            <div className="space-y-1">
              <Text variant="body-xs" color="secondary">
                First name
              </Text>
              <Input
                placeholder="First name"
                value={inviteFirstName}
                onChange={e => setInviteFirstName(e.target.value)}
                className="w-[130px]"
              />
            </div>
            <div className="space-y-1">
              <Text variant="body-xs" color="secondary">
                Last name
              </Text>
              <Input
                placeholder="Last name"
                value={inviteLastName}
                onChange={e => setInviteLastName(e.target.value)}
                className="w-[130px]"
              />
            </div>
            <div className="space-y-1 flex-1">
              <Text variant="body-xs" color="secondary">
                Email
              </Text>
              <Input
                type="email"
                placeholder="Email address"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && inviteValidation.isValid) {
                    handleInviteUser();
                  }
                }}
              />
            </div>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <div>
                  <Button
                    onClick={handleInviteUser}
                    disabled={!inviteValidation.isValid || isInviting}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Invite
                  </Button>
                </div>
              </TooltipTrigger>
              {!inviteValidation.isValid && inviteEmail.trim() !== '' && (
                <TooltipContent>
                  <Text variant="body-xs">{inviteValidation.error}</Text>
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        </FlexCol>
      )}
    </FlexCol>
  );
};
